// Phase 10 — Reading List route. Read-only list view with status badges.

import * as React from 'react';
import { copy } from '@dailydeck/shared/copy';
import { useReadingList } from '../../hooks/useReadingList.js';
import { Card, CardContent } from '../../components/ui/Card.js';
import { SkeletonRow } from '../../components/ui/Skeleton.js';
import { EmptyState } from '../../components/ui/EmptyState.js';
import { Button } from '../../components/ui/Button.js';

const STATUS_LABELS: Record<string, string> = {
  'to-read': copy.reading.statusToRead,
  reading: copy.reading.statusReading,
  done: copy.reading.statusDone,
};

const STATUS_COLORS: Record<string, string> = {
  'to-read': 'bg-surface-raised text-text-secondary border-border-default',
  reading: 'bg-accent-subtle text-accent-default border-transparent',
  done: 'bg-state-success text-text-on-accent border-transparent',
};

function Rating({ value }: { value: number | null }) {
  if (value == null) return null;
  return (
    <span className="text-caption text-text-muted" aria-label={`${value} out of 5`}>
      {'★'.repeat(value)}{'☆'.repeat(5 - value)}
    </span>
  );
}

export function ReadingList(): JSX.Element {
  React.useEffect(() => {
    document.title = `DailyDeck — ${copy.reading.pageTitle}`;
  }, []);

  const query = useReadingList();

  return (
    <div className="space-y-6" data-testid="reading-route">
      <header>
        <h1 className="text-title-1 font-semibold text-text-primary">
          {copy.reading.pageTitle}
        </h1>
        <p className="text-body text-text-secondary mt-1">
          {copy.reading.pageSubtitle}
        </p>
      </header>

      {query.isPending && (
        <div className="rounded-lg border border-border-subtle bg-surface-raised" aria-label={copy.reading.loadingLabel}>
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </div>
      )}

      {query.isError && (
        <Card>
          <CardContent className="py-4">
            <p className="text-body-strong text-text-primary">{copy.reading.errorTitle}</p>
            <p className="text-caption text-text-secondary mt-1">{copy.errors.network_unavailable}</p>
            <Button variant="secondary" className="mt-3" onClick={() => void query.refetch()}>
              {copy.reading.retry}
            </Button>
          </CardContent>
        </Card>
      )}

      {query.isSuccess && query.data.length === 0 && (
        <EmptyState title={copy.reading.emptyTitle} description={copy.reading.emptyBody} />
      )}

      {query.isSuccess && query.data.length > 0 && (
        <div className="space-y-2">
          {query.data.map((item) => (
            <Card key={item.id}>
              <CardContent className="flex items-start justify-between gap-4 py-4">
                <div className="min-w-0 flex-1">
                  <h3 className="text-body-strong font-medium text-text-primary">
                    {item.title}
                  </h3>
                  {item.author && (
                    <p className="text-caption text-text-secondary">{item.author}</p>
                  )}
                  {item.notes && (
                    <p className="text-caption text-text-muted mt-1 line-clamp-2">
                      {item.notes}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <span
                    className={`inline-flex rounded-full border px-2 py-0.5 text-micro font-medium ${STATUS_COLORS[item.status] ?? STATUS_COLORS['to-read']}`}
                  >
                    {STATUS_LABELS[item.status] ?? item.status}
                  </span>
                  <Rating value={item.rating} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
